import React, { useState } from 'react';
import { XIcon, AlertCircleIcon } from 'lucide-react';
interface CancelLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, reasonNote?: string) => void;
  lessonTitle?: string;
  studentName?: string;
  darkMode: boolean;
}
export const CancelLessonModal: React.FC<CancelLessonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  lessonTitle,
  studentName,
  darkMode
}) => {
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelReasonNote, setCancelReasonNote] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!isOpen) return null;
  const handleConfirm = async () => {
    if (!cancelReason) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm(cancelReason, cancelReasonNote);
      // Reset form
      setCancelReason('');
      setCancelReasonNote('');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (!isSubmitting) {
      setCancelReason('');
      setCancelReasonNote('');
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-2031">
      <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 w-full max-w-md`} onClick={e => e.stopPropagation()} data-id="element-2032">
        {/* Header */}
        <div className="flex justify-between items-center mb-4" data-id="element-2033">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-2034">
            Cancel lesson?
          </h3>
          <button onClick={handleClose} disabled={isSubmitting} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-50`} data-id="element-2035">
            <XIcon className="h-5 w-5" data-id="element-2036" />
          </button>
        </div>

        {/* Warning message */}
        <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`} data-id="element-2037">
          <div className="flex items-start" data-id="element-2038">
            <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-2039" />
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2040">
              This will mark the lesson as cancelled and keep it in history.
            </p>
          </div>
        </div>

        {/* Lesson details */}
        {(lessonTitle || studentName) && <div className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2041">
            {studentName && <p className="text-sm" data-id="element-2042">
                <span className="font-medium" data-id="element-2043">Student:</span> {studentName}
              </p>}
            {lessonTitle && <p className="text-sm" data-id="element-2044">
                <span className="font-medium" data-id="element-2045">Lesson:</span> {lessonTitle}
              </p>}
          </div>}

        {/* Reason dropdown */}
        <div className="mb-4" data-id="element-2046">
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-2047">
            Reason <span className="text-red-500" data-id="element-2048">*</span>
          </label>
          <select value={cancelReason} onChange={e => {
          setCancelReason(e.target.value);
          // Clear note if not "Other"
          if (e.target.value !== 'Other') {
            setCancelReasonNote('');
          }
        }} disabled={isSubmitting} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300 focus:ring-deep-pink focus:border-deep-pink'} disabled:opacity-50`} required data-id="element-2049">
            <option value="" data-id="element-2050">Select a reason...</option>
            <option value="Student sick" data-id="element-2051">Student sick</option>
            <option value="Teacher unavailable" data-id="element-2052">Teacher unavailable</option>
            <option value="Scheduling conflict" data-id="element-2053">Scheduling conflict</option>
            <option value="Other" data-id="element-2054">Other</option>
          </select>
        </div>

        {/* Optional note field (shown when "Other" is selected) */}
        {cancelReason === 'Other' && <div className="mb-4" data-id="element-2055">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-2056">
              Reason (optional)
            </label>
            <textarea value={cancelReasonNote} onChange={e => setCancelReasonNote(e.target.value)} disabled={isSubmitting} placeholder="Please provide additional details..." className={`w-full px-3 py-2 rounded-lg border min-h-[80px] ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400 focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300 placeholder-gray-500 focus:ring-deep-pink focus:border-deep-pink'} disabled:opacity-50`} data-id="element-2057" />
          </div>}

        {/* Action buttons */}
        <div className="flex justify-end gap-3" data-id="element-2058">
          <button onClick={handleClose} disabled={isSubmitting} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'} disabled:opacity-50`} data-id="element-2059">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={!cancelReason || isSubmitting} className={`px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-2060">
            {isSubmitting ? 'Cancelling...' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>;
};